import { MapContext, Polygon as GoogleMapsPolygon } from '@react-google-maps/api'

import isEmpty from 'lodash/isEmpty'

import { unregisterEvents, applyUpdatersToPropsAndRegisterEvents } from '../../utils/helper'

const eventMap = {
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick'
}

const updaterMap = {
  draggable(instance: google.maps.Polygon, draggable: boolean): void {
    instance.setDraggable(draggable)
  },
  editable(instance: google.maps.Polygon, editable: boolean): void {
    instance.setEditable(editable)
  },
  map(instance: google.maps.Polygon, map: google.maps.Map): void {
    instance.setMap(map)
  },
  options(instance: google.maps.Polygon, options: google.maps.PolygonOptions): void {
    instance.setOptions(options)
  },
  path(
    instance: google.maps.Polygon,
    path:
      | google.maps.MVCArray<google.maps.LatLng>
      | google.maps.LatLng[]
      | google.maps.LatLngLiteral[]
  ): void {
    instance.setPath(path)
  },

  paths(
    instance: google.maps.Polygon,
    paths:
      | google.maps.MVCArray<google.maps.LatLng>
      | google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>
      | google.maps.LatLng[]
      | google.maps.LatLng[][]
      | google.maps.LatLngLiteral[]
      | google.maps.LatLngLiteral[][]
  ): void {
    instance.setPaths(paths)
  },

  visible(instance: google.maps.Polygon, visible: boolean): void {
    instance.setVisible(visible)
  }
}

class Polygon extends GoogleMapsPolygon {
  // eslint-disable-next-line react/static-property-placement
  static contextType = MapContext

  polygon = null

  lastPath = null

  lastCenter = null

  registeredEvents = []

  getPosition() {
    const path = this.getPath()

    if (this.lastPath === path) {
      return this.lastCenter
    }
    this.lastPath = path

    // eslint-disable-next-line no-undef
    const bounds = new google.maps.LatLngBounds()
    path.forEach(latlng => {
      bounds.extend(latlng)
    })

    this.lastCenter = bounds.getCenter()
    return this.lastCenter
  };

  componentDidMount() {
    const polygonOptions = {
      ...(this.props.options || {}),
      ...(this.props.clusterer ? {} : { map: this.context }),
      path: this.props.paths
    }

    // Unfortunately we can't just do this in the contstructor, because the
    // `MapContext` might not be filled in yet.
    // eslint-disable-next-line no-undef
    this.polygon = new google.maps.Polygon(polygonOptions)
    this.polygon.getPosition = this.getPosition.bind(this.polygon)
    this.polygon.setMap(this.context)
    this.polygon.getDraggable = () => false

    if (!isEmpty(this.props.clusterer)) {
      this.props.clusterer.addMarker(
        this.polygon,
        !!this.props.noClustererRedraw
      )
    } else {
      this.polygon.setMap(this.context)
    }

    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap,
      eventMap,
      prevProps: {},
      nextProps: this.props,
      instance: this.polygon
    })

    if (this.props.onLoad) {
      this.props.onLoad(this.polygon)
    }
  }

  componentDidUpdate(prevProps) {
    if (this.polygon) {
      unregisterEvents(this.registeredEvents)

      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap,
        eventMap,
        prevProps,
        nextProps: this.props,
        instance: this.polygon
      })
    }
  }

  componentWillUnmount() {
    if (this.polygon) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.polygon)
      }

      unregisterEvents(this.registeredEvents)

      if (!isEmpty(this.props.clusterer)) {
        this.props.clusterer.removeMarker(this.polygon, !!this.props.noClustererRedraw)
      } else {
        this.polygon && this.polygon.setMap(null)
      }
    }
  }
}

export default Polygon
